import { computed } from 'vue';
import api from '@/helpers/api';
import apiHeader from '@/helpers/apiHeader';
import generalMasterDataService from '@/services/generalMasterData/generalMasterDataService';
import formConfig from '@/helpers/New_formConfig';

/**
 * Lapisan stateful form engine (select options, dependency reset, preload label, default_value).
 * Evaluasi kondisi murni tetap di helpers/formConfig.js (SATU-SATUNYA sumber kebenaran FE).
 *
 * Semua dependensi dikirim lewat parameter (bukan `this`), dan berupa GETTER supaya bisa dipanggil
 * dari `setup()` di Options API sebelum `data()` terbentuk -- nilainya baru dibaca saat fungsi dipakai.
 *
 * Context evaluator (`formConfigCtx`) dibangun composable ini dari `getDialog().form` + `getMcrType`,
 * jadi form tidak perlu menulis ulang. Kalau di-return, bisa dibaca di komponen lewat `this.formConfigCtx`.
 *
 * @param {Object}   deps
 * @param {Object}   deps.vm                  instance komponen (dipakai `api(vm, ...)` buat loading/toast global)
 * @param {Function} deps.getFormConfig       () => config form (hasil resolve BE)
 * @param {Function} deps.getDialog           () => { form, options, loading, loaded, errors }
 * @param {Function} deps.getMcrType          () => mcr_type aktif (sumbernya beda tiap form: prop / store)
 * @param {Function} [deps.isView]            () => boolean, form read-only (default false)
 * @param {Object}   [deps.fieldResolvers]    { nama_field: () => value } override cara baca 1 field di ctx.getField
 *                                            (mis. material_number yang baru dianggap terisi setelah dipilih)
 * @param {Function} [deps.getCtx]            override PENUH ctx (default: dibangun composable ini)
 * @param {Function} [deps.getDetailSources]  () => registry cache detail, mis. { materialLookupDetail }
 * @param {Set}      [deps.searchAsYouTypeFields] field dataset besar (preload label cuma fetch 1 value)
 *
 * Dukungan field repeatable per BARIS (opt-in, dipakai form FORM02 -- New_CataloguerForm & tab-nya): opsi
 * `row` + `optionsKey` di loadSelectFieldOptions, isColumnDependencyReadyFn, onColumnDependencyChange.
 * Pemanggil yang tidak mengirim `row`/`optionsKey` mendapat perilaku lama persis (lihat DECISIONS_LOG.md
 * "Scope `depends_on` ... di dalam `columns`").
 */
export function useFormConfigEngine({
	vm,
	getFormConfig,
	getDialog,
	getMcrType = () => '',
	isView = () => false,
	fieldResolvers = {},
	getCtx: customGetCtx,
	getDetailSources = () => ({}),
	searchAsYouTypeFields = new Set(),
}) {
	const searchDebounceTimers = {};
	// Endpoint ber-placeholder (`{id}`): URL terakhir yang dimuat per optionsKey -- opsi dimuat ulang kalau URL-nya berubah
	// (mis. Item Name diganti tanpa reset value), dan request yang sedang berjalan per key (supaya bisa ditunggu).
	const requestUrlByKey = {};
	const inflightByKey = {};

	// ===== Context evaluator =====
	// userAuth dibaca lazy (bukan sekali di module scope) supaya tidak basi kalau sesi berubah.
	const getUser = () => {
		try { return JSON.parse(localStorage.getItem('userAuth') || '{}')?.user || {}; } catch { return {}; }
	};

	const getUserRoles = () => {
		try {
			const roles = JSON.parse(localStorage.getItem('userAuth') || '{}')?.userHasRoles;
			// Nama role saja (string / objek { name }), sama dengan New_McrFooter.userRoleNames.
			return Array.isArray(roles) ? roles.map((role) => (typeof role === 'string' ? role : role?.name)).filter(Boolean) : [];
		} catch { return []; }
	};

	const buildCtx = () => ({
		isView: !!isView(),
		getField: (name) => {
			if (fieldResolvers[name]) return fieldResolvers[name]();
			if (name === 'mcr_type') return (getMcrType() || '').toUpperCase();
			if (name === 'client') return getUser().client_code || '';
			if (name === 'company') return getUser().company_code || '';
			// Context murni FE: section user yang login (mis. override MRP Controller INALUM per section, `row_autofill.overrides`).
			// Sama seperti form_mode: JANGAN dipakai di visible/enabled/rules -- BE tidak mengenalnya.
			if (name === 'user_section') return getUser().section_code || '';
			// Context murni FE: district user yang login (ANTAM, dari token SSO; company lain kosong). Cuma untuk param data_source
			// (mis. opsi plant = plant district user) -- JANGAN dipakai di visible/enabled/rules, BE tidak mengenalnya.
			if (name === 'user_district') return getUser().district_code || '';
			// Context daftar role user yang login (array; `in`/`not_in` cocok kalau SALAH SATU role ada di daftar),
			// mis. kunci Item Name / Item Type / Characteristic untuk Inventory Control (`readonlyandsaved`). BE FORM02 juga mengenalnya
			// (validasi & Guard, `FormUserRoles`) -- aturan pakai di FORM_CONFIG_SCHEMA.md "Context user_roles".
			if (name === 'user_roles') return getUserRoles();
			// Context murni FE: form sedang bisa diedit ("edit") atau lihat-saja ("view": halaman search, material-request,
			// draft non-draft). BE tidak mengenalnya (null) -- aturan pakainya di FORM_CONFIG_SCHEMA.md "Context form_mode".
			if (name === 'form_mode') return isView() ? 'view' : 'edit';
			return getDialog().form?.[name];
		},
		hasLine: (section) => {
			const rows = getDialog().form?.[section];
			return Array.isArray(rows) && rows.length > 0;
		},
		// Baris section (field tabel) -- dipakai `has_line` + `where`.
		getRows: (section) => {
			const rows = getDialog().form?.[section];
			return Array.isArray(rows) ? rows : [];
		},
		// Generic: baca 1 atribut tambahan dari OPSI yang lagi kepilih di suatu field (bukan value form-nya) --
		// dipakai resolveDataSourceParams() buat entry `{ from, attribute }` di data_source.params.
		getFieldOption: (field, attribute) => {
			const dialog = getDialog();
			const selected = (dialog.options[field] || [])
				.find((opt) => String(opt.value) === String(dialog.form?.[field]));
			return selected ? selected[attribute] : undefined;
		},
	});

	const formConfigCtx = computed(buildCtx);
	const getCtx = customGetCtx || (() => formConfigCtx.value);

	// Ctx per baris field repeatable: nilai kolom di baris itu dipakai dulu, baru context form -- semantik
	// sama dengan FormConfigGuard::sanitizeTableRows di BE. Tanpa `row` -> ctx form biasa (perilaku lama).
	// Logika-nya di helper withRowCtx (satu sumber dengan validasi Save New_McrFooter).
	const buildRowCtx = (row) => formConfig.withRowCtx(getCtx(), row);

	// ===== Wrapper evaluator (tab wajib diisi pemanggil) =====
	const isFieldVisibleFn = (field, tab) => formConfig.isFieldVisible(getFormConfig(), tab, field, getCtx());
	const isFieldEnabledFn = (field, tab) => formConfig.isFieldEnabled(getFormConfig(), tab, field, getCtx());
	const isFieldMandatoryFn = (field, tab) => formConfig.isFieldMandatory(getFormConfig(), tab, field, getCtx());
	const isSectionMandatoryFn = (tab) => formConfig.isSectionMandatory(getFormConfig(), tab, getCtx());
	const isFieldDependencyReadyFn = (field, tab) => formConfig.isDependencyReady(getFormConfig(), tab, field, getCtx());
	const isFieldInvalidFn = (field) => {
		const dialog = getDialog();
		return !!dialog.errors[field] && !formConfig.isFilledValue(dialog.form[field]);
	};
	const isColumnInvalidFn = (row, column) => !!row.rowErrors?.[column] && !formConfig.isFilledValue(row[column]);
	const isTabVisibleFn = (tab) => formConfig.isTabVisible(getFormConfig(), tab, getCtx());
	const isColumnVisibleFn = (column, field, tab) => formConfig.isColumnVisible(getFormConfig(), tab, field, column, getCtx());
	const isColumnEnabledFn = (column, field, tab) => formConfig.isColumnEnabled(getFormConfig(), tab, field, column, getCtx());
	const isColumnMandatoryFn = (column, field, tab) => formConfig.isColumnMandatory(getFormConfig(), tab, field, column, getCtx());
	// `depends_on` di config kolom, dicek terhadap baris `row` (mis. location menunggu plant di baris yang sama).
	const isColumnDependencyReadyFn = (column, field, tab, row) =>
		formConfig.isColumnDependencyReady(getFormConfig(), tab, field, column, buildRowCtx(row));

	const getSelectValueLabelFn = (options, value) => {
		if (value == null || value === '') return '';

		const raw = (typeof value === 'object') ? (value.label || value.value || value.code || '') : value;
		const v = String(raw);
		if (!v) return '';
		const match = (options || []).find((o) => String(o.value) === v);
		return match ? match.label : v;
	};

	// source_field boleh dot-path (mis. "material_type.code") buat nested object di response detail.
	const resolveSourcePath = (source, path) => {
		if (source == null || !path) return undefined;
		return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), source);
	};

	// source_field boleh array (mis. ["short_text", "po_text_vertical"]) buat gabung beberapa key jadi satu
	// value -- `join` nentuin separatornya (default newline).
	const resolveSourceValue = (source, sourceField, join) => {
		if (!Array.isArray(sourceField)) return resolveSourcePath(source, sourceField);
		const parts = sourceField
			.map((path) => resolveSourcePath(source, path))
			.filter((v) => v !== undefined && v !== null && v !== '');
		return parts.length ? parts.join(join ?? '\n') : undefined;
	};

	// ===== Load options =====
	// 1. `force` -- lewatin cache loaded (dipakai field yang harus load tiap ketik).
	// 2. `mapItem` -- override mapping response -> option (default {code, description} -> {value, label} +
	//    `data_source.extra_fields`; nama kolom code/description bisa diganti lewat `data_source.value_field`/
	//    `description_field`, jadi field baru TIDAK perlu mapItem custom cuma karena nama kolomnya beda).
	// 3. `tab` wajib diisi. `column` -- data_source di level kolom field repeatable.
	// 4. `search` -- kirim `search` + `limit` ke server (field dataset besar).
	// 5. `merge` -- gabung ke dialog.options[field] yang ada (dedup by value), bukan replace.
	// 6. `row` (opsional) -- baris field repeatable: params/filter kolom dibaca dari baris itu dulu (buildRowCtx),
	//    dan `depends_on` KOLOM ikut dicek. Tanpa `row`, depends_on kolom tidak dicek (perilaku lama --
	//    form FORM03/04/06 mengecek dependency kolomnya sendiri sebelum memanggil).
	// 7. `optionsKey` (opsional, default `field`) -- key di dialog.options/loading/loaded. Diisi kalau 1 field
	//    repeatable punya >1 kolom select, atau opsinya beda per baris (mis. `${field}.${column}.${index}`).
	const loadSelectFieldOptions = async (field, { force = false, mapItem, tab, column, search, merge = false, row, optionsKey = field } = {}) => {
		const dialog = getDialog();
		const config = getFormConfig();
		const ctx = buildRowCtx(row);
		const key = optionsKey;

		// `fallback`: param `required` kosong -> data_source pengganti (tanpa fallback: null = tidak fetch). Tanpa `required` = apa adanya.
		const dataSource = formConfig.resolveDataSourceFallback(column
			? formConfig.getColumnConfig(config, tab, field, column)?.data_source
			: formConfig.getFieldDataSource(config, tab, field), ctx);
		// Endpoint tanpa placeholder: cache seperti dulu (sudah dimuat / sedang dimuat -> lewati). Endpoint ber-placeholder:
		// "sudah dimuat" baru dicek setelah URL-nya diketahui (di bawah), supaya param path yang berubah memicu muat ulang.
		const isPathTemplate = !!dataSource?.endpoint && dataSource.endpoint.includes('{');
		// Request-nya bisa berubah walau field tidak di-reset: endpoint ber-placeholder, atau ada param bersyarat (`when`).
		// Untuk data_source seperti ini "sudah dimuat" dicek dari tanda request terakhir (di bawah), bukan flag loaded saja.
		// `option_values` bersyarat (field biasa) ikut membuat "param-aware": daftar dimuat ulang kalau batasannya berubah
		// (mis. Material Type diganti ke/dari ZFGD).
		// Kolom tabel: dievaluasi dengan ctx baris (`row`), mis. Valuation Area FORM02 INALUM tanpa Plant NV.
		const optionRule = formConfig.resolveOptionRule(config, tab, field, ctx, column);
		// Param `{ from, attribute }` di FIELD biasa (kolom tabel tidak: tab FORM02 memuat opsi `from` sendiri & kunci opsi kolomnya sudah
		// memuat nilai param): nilainya atribut OPSI field lain, bisa berubah tanpa field ini di-reset (mis. Material Type FORM01 MCR003
		// diisi lookup Material Number, bukan @change) -> "param-aware", dan opsi field `from` dimuat dulu kalau belum ada (tanpa itu
		// filter terkirim kosong = daftar tanpa filter yang lalu tersimpan di cache).
		const hasFromParam = !column && (dataSource?.params || []).some((entry) => entry && typeof entry === 'object' && entry.from);
		const isParamAware = isPathTemplate
			|| hasFromParam
			|| formConfig.hasConditionalOptionValues(config, tab, field, column)
			|| (dataSource?.params || []).some((entry) => entry && typeof entry === 'object' && (entry.when || Array.isArray(entry.join)));
		const allowOption = (option) => formConfig.isOptionValueAllowed(optionRule, option.value);
		if (!force && (dialog.loading[key] || (dialog.loaded[key] && !isParamAware))) return;
		const dependencyReady = column
			? (!row || formConfig.isColumnDependencyReady(config, tab, field, column, ctx))
			: formConfig.isDependencyReady(config, tab, field, ctx);
		if (!dataSource || !dependencyReady) return;

		if (dataSource.type === 'array') {
			dialog.options[key] = (dataSource.options || []).map((item) => ({
				value: item.code,
				label: item.label || `${item.code} - ${item.description}`
			})).filter(allowOption);
			dialog.loaded[key] = true;
			return;
		}

		// "Copy From" -- opsi diambil dari array di cache detail (bukan fetch API baru), lalu di-dedup.
		if (dataSource.type === 'detail_array') {
			const detail = getDetailSources()[dataSource.source || 'materialLookupDetail'];
			const rows = Array.isArray(detail?.[dataSource.source_array])
				? detail[dataSource.source_array]
				: [];
			const filter = dataSource.filter || {};
			const filtered = rows.filter((row) =>
				Object.entries(filter).every(([rowKey, ctxField]) => {
					const ctxValue = ctx.getField(ctxField);
					if (!formConfig.isFilledValue(ctxValue)) return true; // belum dipilih -> jangan nyaring dulu
					return String(resolveSourcePath(row, rowKey) ?? '') === String(ctxValue ?? '');
				})
			);
			const seen = new Set();
			dialog.options[key] = filtered
				.map((row) => resolveSourcePath(row, dataSource.source_field))
				.filter((v) => v !== undefined && v !== null && v !== '' && !seen.has(v) && seen.add(v))
				.map((v) => ({ value: v, label: String(v) }))
				.filter(allowOption);
			dialog.loaded[key] = true;
			return;
		}

		// Call endpoint
		dialog.loading[key] = true;
		let releaseInflight;
		inflightByKey[key] = new Promise((resolve) => { releaseInflight = resolve; });
		try {
			let endpoint = dataSource.endpoint;
			if (hasFromParam && !isPathTemplate) await ensureParamSourceOptions(dataSource, tab);
			let params = formConfig.resolveDataSourceParams(dataSource, ctx);
			if (isPathTemplate) {
				// Placeholder diisi dari params (mis. `{id}` <- id opsi Item Name terpilih). Kalau param dari OPSI field lain
				// (`{ from, attribute }`) belum ada karena opsi field itu belum dimuat, muat dulu lalu hitung ulang.
				let resolved = formConfig.resolveEndpoint(endpoint, params);
				if (!resolved.ready) {
					await ensureParamSourceOptions(dataSource, tab);
					params = formConfig.resolveDataSourceParams(dataSource, ctx);
					resolved = formConfig.resolveEndpoint(endpoint, params);
				}
				if (!resolved.ready) return;
				endpoint = resolved.url;
				params = resolved.params;
			}
			const signature = isParamAware
				? `${endpoint}?${JSON.stringify(Object.keys(params).sort().map((name) => [name, params[name]]))}|${JSON.stringify(optionRule)}`
				: null;
			if (isParamAware && !force && dialog.loaded[key] && requestUrlByKey[key] === signature) return;
			const resource = endpoint.split('/').filter(Boolean).pop(); // last segment url, mis. material-type
			if (search !== undefined) {
				params.search = search;
				params.limit = 20;
			}
			// Endpoint general-master-data/{resource} lewat service generic; endpoint lain (mis. dictionary/...)
			// dipanggil langsung sesuai path di config (tanpa prefix /api/).
			const request = endpoint.startsWith('/api/general-master-data/')
				? generalMasterDataService.list(resource, params)
				: apiHeader.get(endpoint.replace(/^\/api\//, ''), { params });
			const resp = await api(vm, request, false);
			// `list_path` (opsional): array opsi ada di dalam response (mis. detail Item Name -> "item_name_material_type").
			// Tanpa key ini: sebagian endpoint bungkus pagination Laravel ({ data: { data: [...] } }), sebagian flat.
			const listFromPath = dataSource.list_path ? resolveSourcePath(resp?.data, dataSource.list_path) : undefined;
			const list = dataSource.list_path ? (Array.isArray(listFromPath) ? listFromPath : [])
				: Array.isArray(resp?.data?.data) ? resp.data.data
				: Array.isArray(resp?.data) ? resp.data
				: Array.isArray(resp) ? resp
				: [];
			// `value_field`/`description_field` (opsional, default "code"/"description") -- nama kolom master data yang
			// tidak seragam (mis. item_names: inc/item_name). Boleh dot-path (mis. "material_type_detail.description").
			// Tanpa kedua key itu perilakunya sama persis dengan dulu.
			const valueField = dataSource.value_field || 'code';
			const descriptionField = dataSource.description_field || 'description';
			const pick = (item, path) => (path.includes('.') ? resolveSourcePath(item, path) : item[path]);
			// `label_field` (opsional): label opsi = kolom itu saja, tanpa kode di depannya (mis. nilai Characteristic: kodenya
			// CINV-xxx yang tidak bermakna bagi user). Tanpa key ini label tetap "<value> - <description>".
			const mapped = list.map(mapItem || ((item) => {
				// Daftar berisi nilai polos (mis. target ConfigDataMapping SPAREPART_CATEGORY ["Insurance", "No Planning"]):
				// value = label = nilai itu. Dulu daftar seperti ini menghasilkan opsi kosong, jadi tidak ada perilaku lama yang berubah.
				if (item === null || typeof item !== 'object') return { value: item, label: String(item ?? '') };
				const option = {
					value: pick(item, valueField),
					// `label_field` didahulukan atas `label` bawaan response (mis. baris district_plants membawa label "AT10 - A101",
					// opsinya pakai "plant.label"). Pemakai label_field lama (nilai Characteristic) tidak membawa `label`.
					label: dataSource.label_field
						? String(pick(item, dataSource.label_field) ?? '')
						: (item.label || `${pick(item, valueField)} - ${pick(item, descriptionField) || ''}`.trim())
				};
				// extra_fields boleh dot-path (mis. "material_type_detail.account_category_ref"); disimpan dengan key path itu apa adanya.
				(dataSource.extra_fields || []).forEach((extraKey) => { option[extraKey] = pick(item, extraKey); });
				return option;
			})).filter(allowOption);
			if (merge) {
				const existing = dialog.options[key] || [];
				const seen = new Set(existing.map((o) => String(o.value)));
				dialog.options[key] = [...existing, ...mapped.filter((o) => !seen.has(String(o.value)))];
			} else {
				dialog.options[key] = mapped;
			}
			if (!force) dialog.loaded[key] = true;
			if (isParamAware) requestUrlByKey[key] = signature;
		} catch (error) {
			dialog.options[key] = [];
		} finally {
			dialog.loading[key] = false;
			delete inflightByKey[key];
			releaseInflight();
		}
	};

	// Param `{ from, attribute }` membaca atribut OPSI terpilih field `from` (mis. id Item Name). Kalau opsi itu belum ada
	// (item baru dibuka, label belum selesai dimuat), tunggu request yang sedang jalan / muat opsi yang cocok dengan
	// value-nya (merge, tidak menghapus opsi lain). Dipanggil untuk endpoint ber-placeholder dan field biasa ber-param `from`.
	// Field `from` boleh milik tab lain (mis. Valuation Class tab Accounting <- Material Type tab Basic Data) -> dimuat dengan config
	// tab asalnya (tab ini dulu kalau field itu ada di sini).
	const ensureParamSourceOptions = async (dataSource, tab) => {
		const dialog = getDialog();
		const config = getFormConfig();
		for (const entry of dataSource?.params || []) {
			if (!entry || typeof entry !== 'object' || !entry.from) continue;
			const value = dialog.form?.[entry.from];
			if (!formConfig.isFilledValue(value)) continue;
			const hasOption = () => (dialog.options[entry.from] || []).some((o) => String(o.value) === String(value));
			// eslint-disable-next-line no-await-in-loop
			if (inflightByKey[entry.from]) await inflightByKey[entry.from];
			if (hasOption()) continue;
			const sourceTab = formConfig.getFieldDataSource(config, tab, entry.from)
				? tab
				: Object.keys(config?.tab_fields || {}).find((name) => formConfig.getFieldDataSource(config, name, entry.from));
			if (!sourceTab) continue;
			// eslint-disable-next-line no-await-in-loop
			await loadSelectFieldOptions(entry.from, {
				tab: sourceTab, force: true, merge: true, ...(searchAsYouTypeFields.has(entry.from) ? { search: String(value) } : {}),
			});
		}
	};

	// Handler generic buat @show semua dropdown.
	const onFieldOptionShow = (field, tab, opts = {}) => loadSelectFieldOptions(field, { tab, ...opts });

	// Search-as-you-type SERVER-SIDE, debounce per `optionsKey` (default `field`). Bind ke `@filter` SelectPrime.
	const searchSelectFieldOptions = (event, field, opts = {}) => {
		const timerKey = opts.optionsKey || field;
		clearTimeout(searchDebounceTimers[timerKey]);
		const query = (event?.value ?? '').trim();
		searchDebounceTimers[timerKey] = setTimeout(() => {
			loadSelectFieldOptions(field, { ...opts, force: true, search: query });
		}, opts.delay ?? 400);
	};

	// ===== Dependency =====
	// Reset field lain yang `depends_on` ke `field`. Daftar dibaca dari config, tidak di-hardcode.
	const onFieldDependencyChange = (field, tab) => {
		const dialog = getDialog();
		const fields = formConfig.getTabFieldMap(getFormConfig(), tab) || {};
		Object.keys(fields).forEach((depField) => {
			// depends_on boleh bersyarat (entry ber-`when`, lihat resolveDependsOn) -- dievaluasi dengan ctx saat ini.
			if (!formConfig.getFieldDependsOn(getFormConfig(), tab, depField, getCtx()).includes(field)) return;
			dialog.loaded[depField] = false;
			dialog.options[depField] = [];
			// Field `multiple` (mis. plant) state-nya DAFTAR -- dikosongkan sebagai [], bukan '' (komponen
			// multi-select/chip menerima nilai yang bukan daftar).
			dialog.form[depField] = formConfig.isFieldMultiple(getFormConfig(), tab, depField, getCtx()) ? [] : '';
		});
	};

	// Versi kolom dari onFieldDependencyChange: kosongkan kolom lain DI BARIS YANG SAMA yang `depends_on`-nya
	// menyebut `column` (dibaca dari config kolom, tidak di-hardcode). Opsinya tidak di-reset di sini karena
	// key opsi per baris ditentukan pemanggil -- kolom seperti itu sebaiknya reload `force` saat @show.
	const onColumnDependencyChange = (column, field, tab, row) => {
		const columns = formConfig.getFieldConfig(getFormConfig(), tab, field)?.columns || {};
		Object.entries(columns).forEach(([depColumn, colConfig]) => {
			if (!Array.isArray(colConfig?.depends_on) || !colConfig.depends_on.includes(column)) return;
			row[depColumn] = '';
		});
	};

	// Reset field yang opsinya diturunkan dari cache detail (data_source.type "detail_array").
	const resetDetailArrayFields = (tabs) => {
		const dialog = getDialog();
		tabs.forEach((tab) => {
			const fields = formConfig.getTabFieldMap(getFormConfig(), tab) || {};
			Object.entries(fields).forEach(([field, fc]) => {
				if (fc?.data_source?.type !== 'detail_array') return;
				dialog.loaded[field] = false;
				dialog.options[field] = [];
				dialog.form[field] = '';
			});
		});
	};

	// `auto_select_single`: field masih kosong & opsinya cuma SATU -> langsung dipilih (lalu field yang depends_on ke
	// field ini di-reset seperti @change biasa). Field tanpa key ini tidak tersentuh.
	const autoSelectSingleOption = async (field, tab) => {
		const dialog = getDialog();
		if (!formConfig.isAutoSelectSingle(getFormConfig(), tab, field, getCtx())) return;
		if (formConfig.isFilledValue(dialog.form?.[field])) return;
		await loadSelectFieldOptions(field, { tab });
		const options = dialog.options[field] || [];
		if (options.length !== 1 || formConfig.isFilledValue(dialog.form?.[field])) return;
		dialog.form[field] = options[0].value;
		onFieldDependencyChange(field, tab);
	};

	// ===== `autofill` field (opt-in, FORM_CONFIG_SCHEMA.md) =====
	// Entry `autofill` di config field `field`: `{ when?, trigger?, endpoint, params, record_path?, fields }` -- setelah `field`
	// berubah, semua `trigger` (default [field]) terisi & `when` cocok -> request `endpoint`/`params` (bahasa param sama dengan
	// data_source), record = `record_path` di data response; field form di `fields` ({ field_form: path_di_record }) diisi.
	// Record tidak ditemukan -> tidak mengubah apa pun. Hasil request di-cache per URL+param (data mapping statis).
	const autofillCache = new Map();
	const applyFieldAutofill = async (field, tab) => {
		if (isView()) return;
		const dialog = getDialog();
		const ctx = getCtx();
		const entries = formConfig.getFieldConfig(getFormConfig(), tab, field)?.autofill;
		for (const entry of Array.isArray(entries) ? entries : []) {
			if (!entry?.endpoint || (entry.when && !formConfig.evaluateCondition(entry.when, ctx))) continue;
			const triggers = [].concat(entry.trigger || field);
			const ready = () => triggers.every((name) => formConfig.isFilledValue(getCtx().getField(name)));
			if (!ready()) continue;
			const resolved = formConfig.resolveEndpoint(entry.endpoint, formConfig.resolveDataSourceParams(entry, ctx));
			if (!resolved.ready || Object.values(resolved.params).some((v) => v === undefined)) continue;
			const cacheKey = `${resolved.url}?${JSON.stringify(Object.keys(resolved.params).sort().map((k) => [k, resolved.params[k]]))}`;
			if (!autofillCache.has(cacheKey)) {
				autofillCache.set(cacheKey, api(vm, apiHeader.get(resolved.url.replace(/^\/api\//, ''), { params: resolved.params }), false)
					.then((resp) => resp?.data)
					.catch(() => { autofillCache.delete(cacheKey); return null; }));
			}
			// eslint-disable-next-line no-await-in-loop
			const data = await autofillCache.get(cacheKey);
			const record = entry.record_path ? resolveSourcePath(data, entry.record_path) : data;
			// Pemicu sudah berubah / dikosongkan selama request berjalan -> hasil dibuang.
			if (!record || typeof record !== 'object' || !ready()) continue;
			Object.entries(entry.fields || {}).forEach(([target, path]) => {
				const value = resolveSourcePath(record, path);
				dialog.form[target] = value === undefined || value === null ? '' : value;
				resolveSelectedFieldLabel(target, tab, dialog.form[target]);
			});
		}
	};

	// Field ber-`auto_select_single` (tab mana pun) yang `depends_on` salah satu `changedFields` -> dikosongkan (berikut field
	// yang depends_on field itu), opsinya dimuat ulang, opsi tunggal dipilih otomatis, lalu `autofill`-nya dijalankan. Untuk
	// field sumber yang ada di tab LAIN (mis. Material Type / Plant di Basic Data -> Sparepart Category di MRP): onFieldDependencyChange
	// cuma memeriksa satu tab. Field tanpa `auto_select_single` tidak disentuh (perilaku form lain tetap). Pemanggil yang
	// menentukan KAPAN nilai sumber dianggap berubah (bukan saat hydrate edit).
	const refreshAutoSelectDependents = async (changedFields) => {
		if (isView()) return;
		const dialog = getDialog();
		const config = getFormConfig();
		const changed = [].concat(changedFields || []);
		const jobs = [];
		Object.keys(config?.tab_fields || {}).forEach((tab) => {
			Object.keys(formConfig.getTabFieldMap(config, tab) || {}).forEach((field) => {
				if (!formConfig.isAutoSelectSingle(config, tab, field, getCtx())) return;
				if (!formConfig.getFieldDependsOn(config, tab, field, getCtx()).some((name) => changed.includes(name))) return;
				dialog.loaded[field] = false;
				dialog.options[field] = [];
				dialog.form[field] = '';
				onFieldDependencyChange(field, tab);
				jobs.push((async () => {
					await autoSelectSingleOption(field, tab);
					if (formConfig.isFilledValue(dialog.form[field])) await applyFieldAutofill(field, tab);
				})());
			});
		});
		await Promise.all(jobs);
	};

	// ===== Preload label (fire-and-forget, JANGAN di-await pemanggil) =====
	// Mengembalikan promise muat opsi (boleh diabaikan pemanggil lama) supaya bisa ditunggu kalau perlu.
	const resolveSelectedFieldLabel = (field, tab, value) => {
		const dialog = getDialog();
		if (!formConfig.isFilledValue(value)) return undefined;
		if (dialog.loaded[field] || dialog.loading[field]) return undefined;
		if (!formConfig.getFieldDataSource(getFormConfig(), tab, field)) return undefined;

		if (searchAsYouTypeFields.has(field)) {
			return loadSelectFieldOptions(field, { tab, force: true, search: String(value) });
		}
		return loadSelectFieldOptions(field, { tab, force: true });
	};

	const preloadSelectedFieldLabels = (tabs) => {
		const dialog = getDialog();
		tabs.forEach((tab) => {
			const fields = formConfig.getTabFieldMap(getFormConfig(), tab) || {};
			Object.entries(fields).forEach(([field, fc]) => {
				if (fc.columns) return; // field repeatable, ditangani preloadRepeatableFieldLabels
				resolveSelectedFieldLabel(field, tab, dialog.form[field]);
			});
		});
	};

	// Field repeatable: options 1 list dibagi semua baris -> fetch tiap value unik SEQUENTIAL + merge.
	const preloadRepeatableFieldLabels = async (tabs) => {
		const dialog = getDialog();
		for (const tab of tabs) {
			const fields = formConfig.getTabFieldMap(getFormConfig(), tab) || {};
			for (const [field, fc] of Object.entries(fields)) {
				if (!fc.columns) continue;
				const rows = dialog.form[field] || [];
				if (!rows.length || dialog.loading[field]) continue;

				for (const [column, colConfig] of Object.entries(fc.columns)) {
					if (!colConfig.data_source) continue; // kolom bukan select

					const codes = [...new Set(rows.map((r) => r[column]).filter((v) => formConfig.isFilledValue(v)))];
					for (const code of codes) {
						const known = (dialog.options[field] || []).some((o) => String(o.value) === String(code));
						if (known) continue;
						// eslint-disable-next-line no-await-in-loop
						await loadSelectFieldOptions(field, {
							tab, column, force: true, search: String(code), merge: true,
						});
					}
				}
			}
		}
	};

	// ===== default_value =====
	// type "static" -- sekali saat form init, cuma kalau field masih kosong.
	const applyStaticDefaultValue = (tabs) => {
		const dialog = getDialog();
		tabs.forEach((tab) => {
			const fields = Object.keys(formConfig.getTabFieldMap(getFormConfig(), tab) || {});
			fields.forEach((field) => {
				const entry = formConfig.resolveActiveDefault(getFormConfig(), tab, field, getCtx());
				if (entry?.type === 'static' && !formConfig.isFilledValue(dialog.form[field])) {
					dialog.form[field] = entry.value;
					resolveSelectedFieldLabel(field, tab, entry.value);
				}
			});
		});
	};

	// type "lookup" -- cache detail dibaca per `entry.source` lewat getDetailSources() (registry).
	// Catatan: pemanggil yang harus memasang watcher buat field `trigger` (tidak ada auto-scan).
	const applyLookupDefaults = (tabs) => {
		const dialog = getDialog();
		const ctx = getCtx();
		tabs.forEach((tab) => {
			const fields = Object.entries(formConfig.getTabFieldMap(getFormConfig(), tab) || {});
			fields.forEach(([field, fc]) => {
				// Field repeatable ber-KOLOM (mis. Manufacture -> emcr_manufacturers): default lookup-nya ada DI TIAP
				// kolom, dan satu baris match di source_array jadi SATU BARIS di tabel -- semua kolom ber-source_array
				// diisi dari baris match yang SAMA (inis-nya: manufacturerParts = detail.eu_material_manufacturer.map(...)				// di dialog lama, sekarang config-driven). Matching per kolom: filter tiap kolom dievaluasi terhadap
				// ctx form (material_number/plant), bukan ctx baris.
				const colLookups = (fc && fc.columns)
					? Object.keys(fc.columns)
						.map((column) => ({ column, entry: formConfig.resolveActiveColumnDefault(getFormConfig(), tab, field, column, ctx) }))
						.filter((item) => item.entry?.type === 'lookup')
					: [];
				if (fc && fc.columns) {
					if (!colLookups.length) return;
					const first = colLookups[0].entry;
					const colTriggers = Array.isArray(first.trigger) ? first.trigger : [first.trigger];
					if (!colTriggers.every((name) => formConfig.isFilledValue(ctx.getField(name)))) return;
					const detail = getDetailSources()[first.source || 'materialLookupDetail'];
					if (!detail) return;
					// Match per kolom (bisa beda source_array/filter per kolom). Field tanpa source_array (mis.
					// old_manufacture) tidak mencegah baris kebentuk namanya -- kalau tidak ketemu, kolomnya tetap kosong.
					const perColumn = colLookups.map(({ column, entry }) => {
						let matches = [];
						if (entry.source_array) {
							const rows = Array.isArray(detail[entry.source_array]) ? detail[entry.source_array] : [];
							const entryFilter = entry.filter || {};
							matches = rows.filter((row) =>
								Object.entries(entryFilter).every(([rowKey, ctxField]) => {
									const rowValue = resolveSourcePath(row, rowKey);
									const ctxValue = ctx.getField(ctxField);
									return String(rowValue ?? '') === String(ctxValue ?? '');
								})
							);
						}
						return { column, entry, matches };
					});
					const sourceArrays = perColumn.filter((c) => c.entry.source_array);
					if (sourceArrays.length && sourceArrays.every((c) => c.matches.length === sourceArrays[0].matches.length)) {
						const rowCount = sourceArrays[0].matches.length;
						if (rowCount) {
							dialog.form[field] = Array.from({ length: rowCount }, (_, i) => {
								const row = {};
								Object.keys(fc.columns).forEach((column) => { row[column] = ''; });									perColumn.forEach(({ column, entry, matches }) => {
										const value = resolveSourceValue(
											(entry.source_array ? matches[i] : detail),
											entry.source_field,
											entry.join,
										);
										// null (mis. notes kosong di EU) dianggap tidak punya value -- kolom dibiarkan '',
										// konsisten dengan mapping dialog lama (m.notes || '').
										if (value != null) row[column] = value;
									});
								return row;
							});
						}
					}
					return;
				}

				const entry = formConfig.resolveActiveDefault(getFormConfig(), tab, field, ctx);
				if (entry?.type !== 'lookup') return;

				const triggers = Array.isArray(entry.trigger) ? entry.trigger : [entry.trigger];
				const ready = triggers.every((name) => formConfig.isFilledValue(ctx.getField(name)));
				if (!ready) return;

				const detail = getDetailSources()[entry.source || 'materialLookupDetail'];
				if (!detail) return;

				// Field repeatable TANPA columns (mis. Attachment -> emcr_attachments): `entry.row_fields` memetakan
				// atribut row ({ nama_attr: "<dot path di source_array>" }), SATU row match = SATU baris tabel.
				// Row-nya di-replace (pola == dialog lama), kalau tidak ada match form dibiarkan apa adanya.
				if (entry.row_fields) {
					const rfRows = Array.isArray(detail[entry.source_array]) ? detail[entry.source_array] : [];
					const rfFilter = entry.filter || {};
					const rfMatches = rfRows.filter((row) =>
						Object.entries(rfFilter).every(([rowKey, ctxField]) => {
							const rowValue = resolveSourcePath(row, rowKey);
							const ctxValue = ctx.getField(ctxField);
							return String(rowValue ?? '') === String(ctxValue ?? '');
						})
					);
					if (rfMatches.length) {
						dialog.form[field] = rfMatches.map((match) => {
							const row = {};
							Object.entries(entry.row_fields).forEach(([attr, path]) => {
								row[attr] = resolveSourcePath(match, path) ?? '';
							});
							return row;
						});
					}
					return;
				}

				// source_array + filter: cari baris yang cocok semua kriteria, ambil source_field dari baris itu.
				if (entry.source_array) {
					const rows = Array.isArray(detail[entry.source_array]) ? detail[entry.source_array] : [];
					const filter = entry.filter || {};
					const matches = rows.filter((row) =>
						Object.entries(filter).every(([rowKey, ctxField]) => {
							const rowValue = resolveSourcePath(row, rowKey);
							const ctxValue = ctx.getField(ctxField);
							return String(rowValue ?? '') === String(ctxValue ?? '');
						})
					);
					// Field `multiple` (mis. plant ZFGD MCR002): SEMUA baris yang cocok dipakai sekaligus -- daftar
					// plant material di SAP, satu set nilai MRP dipakai untuk tiap plant itu (lihat UserFormService).
					if (formConfig.isFieldMultiple(getFormConfig(), tab, field, ctx)) {
						const values = [...new Set(
							matches
								.map((row) => resolveSourceValue(row, entry.source_field, entry.join))
								.filter((value) => formConfig.isFilledValue(value))
						)];
						dialog.form[field] = values;
						resolveSelectedFieldLabel(field, tab, values[0]);
						return;
					}
					// Reset ke kosong kalau trigger ready tapi tidak ada baris match (jangan nyangkut value lama).
					const value = resolveSourceValue(matches[0], entry.source_field, entry.join);
					dialog.form[field] = value !== undefined ? value : '';
					resolveSelectedFieldLabel(field, tab, dialog.form[field]);
					return;
				}

				const value = resolveSourceValue(detail, entry.source_field, entry.join);
				if (entry.map) {
					applyLookupMap(field, tab, entry, value);
					return;
				}
				if (value !== undefined) {
					dialog.form[field] = value;
					resolveSelectedFieldLabel(field, tab, value);
				}
			});
		});
	};

	// `default_value` lookup + `map` (opsional, FORM_CONFIG_SCHEMA.md): nilai hasil lookup diterjemahkan dulu lewat SATU request
	// master data -- `map.endpoint` + `map.params` (bahasa param sama dengan data_source) + nilai lookup sebagai param
	// `map.key_param`; record = baris yang kolom `map.key_field` (default key_param) SAMA PERSIS dengan nilai lookup, field diisi
	// kolom `map.source_field` record itu. Tidak ketemu / nilai lookup kosong -> field dikosongkan. Contoh FORM01 MCR002:
	// Standard Description material (kode cataloguer) -> `gm_standard_description_cataloguers.standard_description_user_code`.
	const lookupMapCache = new Map();
	const applyLookupMap = async (field, tab, entry, value) => {
		const dialog = getDialog();
		const map = entry.map;
		const triggers = Array.isArray(entry.trigger) ? entry.trigger : [entry.trigger];
		const snapshot = () => JSON.stringify(triggers.map((name) => getCtx().getField(name) ?? ''));
		const before = snapshot();
		let mapped = '';
		if (formConfig.isFilledValue(value) && map?.endpoint && map.key_param) {
			const params = { ...formConfig.resolveDataSourceParams(map, getCtx()), [map.key_param]: value };
			const url = map.endpoint.replace(/^\/api\//, '');
			const cacheKey = `${url}?${JSON.stringify(Object.keys(params).sort().map((k) => [k, params[k]]))}`;
			if (!lookupMapCache.has(cacheKey)) {
				lookupMapCache.set(cacheKey, api(vm, apiHeader.get(url, { params }), false)
					.then((resp) => resp?.data)
					.catch(() => { lookupMapCache.delete(cacheKey); return null; }));
			}
			const data = await lookupMapCache.get(cacheKey);
			const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
			const keyField = map.key_field || map.key_param;
			const record = list.find((row) => String(resolveSourcePath(row, keyField) ?? '') === String(value));
			mapped = record ? (resolveSourcePath(record, map.source_field) ?? '') : '';
		}
		// Material Number diganti selama request berjalan -> hasil lama dibuang.
		if (snapshot() !== before) return;
		dialog.form[field] = mapped;
		resolveSelectedFieldLabel(field, tab, mapped);
	};

	// `option_values`: nilai terpilih yang tidak lagi termasuk batasan aktif dibuang (mis. Material Type diganti ke ZFGD ->
	// Plant A002 dikosongkan). Field multi (array) cuma dibuang elemennya. Field tanpa `option_values` tidak disentuh.
	const pruneDisallowedFieldValues = (tabs) => {
		const dialog = getDialog();
		const config = getFormConfig();
		const ctx = getCtx();
		(tabs || []).forEach((tab) => {
			Object.keys(formConfig.getTabFieldMap(config, tab) || {}).forEach((field) => {
				const rule = formConfig.resolveOptionRule(config, tab, field, ctx);
				const value = dialog.form?.[field];
				if (!rule || !formConfig.isFilledValue(value)) return;
				if (Array.isArray(value)) {
					const kept = value.filter((v) => formConfig.isOptionValueAllowed(rule, v));
					if (kept.length !== value.length) dialog.form[field] = kept;
				} else if (!formConfig.isOptionValueAllowed(rule, value)) {
					dialog.form[field] = '';
				}
			});
		});
	};

	return {
		// ctx (cuma di-return kalau tidak di-override, supaya tidak bentrok dengan computed `formConfigCtx` milik form)
		...(customGetCtx ? {} : { formConfigCtx }),
		// ctx per baris field repeatable -- dipakai pemanggil yang perlu menghitung hal yang SAMA dengan engine untuk
		// satu baris (mis. kunci cache opsi dari param request, lihat New_CataloguerTab.js).
		buildRowCtx,
		// evaluator wrappers
		isFieldVisibleFn,
		isFieldEnabledFn,
		isFieldMandatoryFn,
		isSectionMandatoryFn,
		isFieldDependencyReadyFn,
		isFieldInvalidFn,
		isColumnInvalidFn,
		isTabVisibleFn,
		isColumnVisibleFn,
		isColumnEnabledFn,
		isColumnMandatoryFn,
		isColumnDependencyReadyFn,
		getSelectValueLabelFn,
		// options
		loadSelectFieldOptions,
		onFieldOptionShow,
		searchSelectFieldOptions,
		// dependency
		onFieldDependencyChange,
		autoSelectSingleOption,
		applyFieldAutofill,
		refreshAutoSelectDependents,
		onColumnDependencyChange,
		resetDetailArrayFields,
		pruneDisallowedFieldValues,
		// preload label
		resolveSelectedFieldLabel,
		preloadSelectedFieldLabels,
		preloadRepeatableFieldLabels,
		// default_value
		applyStaticDefaultValue,
		applyLookupDefaults,
		resolveSourcePath,
		resolveSourceValue,
	};
}

export default useFormConfigEngine;
